import { useMemo } from 'react';
import { Difficulty, DifficultyType, ProgressBar, StyledFlex } from '@tryhackme/thm-ui-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDoorClosed, faFileCertificate } from '@fortawesome/pro-regular-svg-icons';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import { PathOutline, PathOutlineRoom } from 'src/common/interfaces/paths';
import { Roadmap } from 'src/features/hacktivities/learn/components/roadmap';
import { sanitizeAndRenderHtmlString } from 'src/common/helpers';
import { WelcomePath } from 'src/features/welcome/welcome.types';
import { StyledContentWrapper, StyledSubTitle } from '../../congratulations-full-screen.styles';
import { StyledRecommendedPath, StyledRecommendedPathHeader, StyledRecommendedPathHeaderLeftSection, StyledRecommendedPathTitle, StyledRecommendedPathSubtitle, StyledRecommendedPathImage, StyledRecommendedPathImageWrapper, StyledPathAttribute, StyledPathSection, StyledPathSectionItem, StyledPathSectionItemImageAndTitleWrapper, StyledCompletedRoomIcon, StyledPathSections, StyledTitle, StyledModulesWrapper, StyledRoadmapHeader, StyledRoadmapWrapper, StyledRecommendedPathWrapper, ProgressBarAndLabelWrapper, ProgressBarWrapper, ProgressBarLabel } from './enroll-path-step.styles';
export const EnrollPathStep = ({
  showRoadmap,
  recommendedPath,
  pathOutline
}: {
  showRoadmap: boolean;
  recommendedPath?: WelcomePath;
  pathOutline?: PathOutline;
}) => {
  const {
    code: pathCode,
    difficulty
  } = recommendedPath || {};
  const numberOfRooms = useMemo(() => pathOutline?.modules.flatMap(module => module.rooms)?.length, [pathOutline]);
  if (!pathOutline || !difficulty) {
    return null;
  }
  const {
    title,
    image,
    description,
    modules
  } = pathOutline;
  const renderRoomCompletionComponent = ({
    progressPercentage
  }: PathOutlineRoom) => {
    const isRoomCompleted = progressPercentage === 100;
    const isRoomInProgress = progressPercentage !== undefined && progressPercentage > 0;
    if (isRoomCompleted) {
      return <StyledCompletedRoomIcon icon={faCircleCheck} data-testid="completed-room-icon" />;
    }
    if (isRoomInProgress) {
      const roundedProgressPercentage = Math.round(progressPercentage);
      return <ProgressBarAndLabelWrapper>
          <ProgressBarWrapper>
            <ProgressBar value={roundedProgressPercentage} height={10} radius={0.5} />
          </ProgressBarWrapper>
          <ProgressBarLabel data-testid="room-progress-bar-label">{`${roundedProgressPercentage}%`}</ProgressBarLabel>
        </ProgressBarAndLabelWrapper>;
    }
    return null;
  };
  if (showRoadmap) {
    return <StyledRoadmapWrapper>
        <StyledRoadmapHeader>
          <StyledTitle>Ready to level up?</StyledTitle>
          <StyledSubTitle>
            Unlock a whole new level of knowledge in your learning path and achieve your goals.
          </StyledSubTitle>
        </StyledRoadmapHeader>
        <Roadmap recommendedPath={pathCode} variant="welcome" isSmall hideTitleDivider hideHeader />
      </StyledRoadmapWrapper>;
  }
  return <StyledContentWrapper data-content="enroll-path" data-sentry-element="StyledContentWrapper" data-sentry-component="EnrollPathStep" data-sentry-source-file="enroll-path-step.tsx">
      <StyledFlex flexDirection="column" gap="0" data-sentry-element="StyledFlex" data-sentry-source-file="enroll-path-step.tsx">
        <StyledTitle data-sentry-element="StyledTitle" data-sentry-source-file="enroll-path-step.tsx">Ready to level up? </StyledTitle>
        <StyledSubTitle data-sentry-element="StyledSubTitle" data-sentry-source-file="enroll-path-step.tsx">
          Unlock a whole new level of knowledge in your learning path and achieve your goals.
        </StyledSubTitle>
      </StyledFlex>
      <StyledRecommendedPathWrapper data-sentry-element="StyledRecommendedPathWrapper" data-sentry-source-file="enroll-path-step.tsx">
        <StyledRecommendedPath data-sentry-element="StyledRecommendedPath" data-sentry-source-file="enroll-path-step.tsx">
          <StyledRecommendedPathHeader data-sentry-element="StyledRecommendedPathHeader" data-sentry-source-file="enroll-path-step.tsx">
            <StyledRecommendedPathHeaderLeftSection data-sentry-element="StyledRecommendedPathHeaderLeftSection" data-sentry-source-file="enroll-path-step.tsx">
              <StyledRecommendedPathTitle data-sentry-element="StyledRecommendedPathTitle" data-sentry-source-file="enroll-path-step.tsx">{title}</StyledRecommendedPathTitle>
              <StyledRecommendedPathSubtitle data-sentry-element="StyledRecommendedPathSubtitle" data-sentry-source-file="enroll-path-step.tsx">{sanitizeAndRenderHtmlString(description)}</StyledRecommendedPathSubtitle>
              <StyledFlex gap="1.6rem" justifyContent="flex-start" data-sentry-element="StyledFlex" data-sentry-source-file="enroll-path-step.tsx">
                <Difficulty difficulty={(difficulty as DifficultyType)} hasIcon isDarkVersion data-sentry-element="Difficulty" data-sentry-source-file="enroll-path-step.tsx" />
                <StyledPathAttribute data-sentry-element="StyledPathAttribute" data-sentry-source-file="enroll-path-step.tsx">
                  <FontAwesomeIcon icon={faDoorClosed} data-sentry-element="FontAwesomeIcon" data-sentry-source-file="enroll-path-step.tsx" />
                  {numberOfRooms} rooms
                </StyledPathAttribute>
                <StyledPathAttribute data-sentry-element="StyledPathAttribute" data-sentry-source-file="enroll-path-step.tsx">
                  <FontAwesomeIcon icon={faFileCertificate} data-sentry-element="FontAwesomeIcon" data-sentry-source-file="enroll-path-step.tsx" />
                  Certificate of completion
                </StyledPathAttribute>
              </StyledFlex>
            </StyledRecommendedPathHeaderLeftSection>
            <StyledRecommendedPathImageWrapper data-sentry-element="StyledRecommendedPathImageWrapper" data-sentry-source-file="enroll-path-step.tsx">
              <StyledRecommendedPathImage src={image} alt={title} data-sentry-element="StyledRecommendedPathImage" data-sentry-source-file="enroll-path-step.tsx" />
            </StyledRecommendedPathImageWrapper>
          </StyledRecommendedPathHeader>
          <StyledPathSections data-sentry-element="StyledPathSections" data-sentry-source-file="enroll-path-step.tsx">
            <StyledModulesWrapper data-sentry-element="StyledModulesWrapper" data-sentry-source-file="enroll-path-step.tsx">
              {modules.map((module, index) => <StyledPathSection key={module.title}>
                  <h4>SECTION {index + 1}</h4>
                  <p>{module.title}</p>
                  {module.rooms.map(room => <StyledPathSectionItem key={room._id}>
                      <StyledPathSectionItemImageAndTitleWrapper>
                        <img src={room.imageURL} alt={room.title} />
                        {room.title}
                      </StyledPathSectionItemImageAndTitleWrapper>
                      {renderRoomCompletionComponent(room)}
                    </StyledPathSectionItem>)}
                </StyledPathSection>)}
            </StyledModulesWrapper>
          </StyledPathSections>
        </StyledRecommendedPath>
      </StyledRecommendedPathWrapper>
    </StyledContentWrapper>;
};